/**
 * Data hooks + row normalisation for the Stat Tracker page.
 *
 * Two very different payloads have to land in one table:
 *
 *   - `GET /stats/v1/tracker/{event_id}?unit=…` is self-describing — every stat
 *     arrives with its label, category and format, so the rows are a flatten.
 *   - `GET /stats/v1/season?season=…&unit=…` is a bare `key → value` map, and
 *     the keys come in `_total` / `_per_game` pairs. Those pairs are folded into
 *     one row with a "Per Game" column so the sheet reads like a season sheet
 *     and not like two unrelated lists.
 *
 * Rates in the season payload are recomputed from season totals server-side
 * (see `services/stats/src/aggregation/season-rollup.ts`), so nothing here
 * averages an average.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiClient } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import type {
  SeasonRollupFeed,
  StatTrackerFeed,
  StatUnit,
  StatValueFormat,
} from '../../lib/api-types';
import { humanizeStatKey } from '../summary/stat-block';

export const SEASON_VIEWS = ['game', 'season'] as const;
export type TrackerView = (typeof SEASON_VIEWS)[number];

export const SORT_FIELDS = ['category', 'name', 'value'] as const;
export type SortField = (typeof SORT_FIELDS)[number];

/** How many seasons back the selector offers when the API has told us nothing. */
const SEASON_LOOKBACK = 5;

export interface StatSheetRow {
  key: string;
  name: string;
  category: string;
  value: unknown;
  format: StatValueFormat;
  isCustom: boolean;
  /** Season view only: the per-game companion of a `_total` key. */
  perGame: number | null;
}

function readableError(cause: unknown, what: string): string {
  if (ApiError.isApiError(cause)) return cause.message;
  return `The ${what} could not be reached. Check your connection and try again.`;
}

function asFormat(raw: unknown): StatValueFormat {
  const known: readonly string[] = [
    'count',
    'yards',
    'percent',
    'average',
    'ratio',
    'list',
    'numeric',
    'boolean',
    'enum',
    'text',
    'tag',
  ];
  return known.includes(String(raw)) ? (raw as StatValueFormat) : 'count';
}

export function toTrackerRows(feed: StatTrackerFeed | null, unit: StatUnit): StatSheetRow[] {
  if (feed === null) return [];
  const categories =
    feed.statistics.length > 0
      ? feed.statistics
      : (feed.units.find((block) => block.unit === unit)?.categories ?? []);
  return categories.flatMap((group) =>
    group.stats.map((stat) => ({
      key: stat.key,
      name: stat.name,
      category: group.category,
      value: stat.value,
      format: asFormat(stat.format),
      isCustom: stat.is_custom,
      perGame: null,
    }))
  );
}

/** `passing_yards_total` → total row; `passing_yards_per_game` → its companion. */
function seasonFormatFor(key: string): StatValueFormat {
  if (key.endsWith('_percent') || key.endsWith('_rate')) return 'percent';
  if (key.endsWith('_ratio')) return 'ratio';
  if (key.includes('per_attempt') || key.includes('per_play') || key.includes('average')) {
    return 'average';
  }
  return 'count';
}

function seasonCategoryFor(key: string): string {
  if (key.includes('pass') || key.includes('completion') || key.includes('interception')) {
    return 'Passing';
  }
  if (key.includes('rush') || key.includes('run')) return 'Rushing';
  if (key.includes('third_down')) return 'Situational';
  if (key.includes('penalt') || key.includes('fumble') || key.includes('drop')) {
    return 'Discipline';
  }
  if (key.includes('punt') || key.includes('fg') || key.includes('kick')) return 'Special Teams';
  return 'Totals';
}

export function toSeasonRows(feed: SeasonRollupFeed | null, unit: StatUnit): StatSheetRow[] {
  const aggregate = feed?.aggregates[unit];
  if (aggregate === undefined) return [];
  const perGame = new Map<string, number>();
  for (const [key, value] of Object.entries(aggregate)) {
    if (key.endsWith('_per_game') && typeof value === 'number') {
      perGame.set(key.slice(0, -'_per_game'.length), value);
    }
  }

  return Object.entries(aggregate)
    .filter(([key]) => !key.endsWith('_per_game'))
    .map(([key, value]) => {
      const base = key.endsWith('_total') ? key.slice(0, -'_total'.length) : key;
      return {
        key,
        name: humanizeStatKey(base),
        category: seasonCategoryFor(key),
        value,
        format: seasonFormatFor(key),
        isCustom: false,
        perGame: perGame.get(base) ?? null,
      };
    });
}

export interface TrackerFeedState {
  feed: StatTrackerFeed | null;
  rows: StatSheetRow[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/** Single-game sheet. `eventId === null` parks the hook without a request. */
export function useTrackerFeed(
  apiClient: ApiClient,
  eventId: string | null,
  unit: StatUnit
): TrackerFeedState {
  const [feed, setFeed] = useState<StatTrackerFeed | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (eventId === null) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getStatTracker(eventId, unit, { signal: controller.signal })
      .then((next) => {
        if (controller.signal.aborted) return;
        setError(null);
        setFeed(next);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(readableError(cause, 'stat tracker'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, eventId, unit, refreshToken]);

  const rows = useMemo(() => toTrackerRows(feed, unit), [feed, unit]);
  return {
    feed,
    rows,
    isLoading,
    error,
    reload: useCallback(() => setRefreshToken((token) => token + 1), []),
  };
}

export interface SeasonRollupState {
  feed: SeasonRollupFeed | null;
  rows: StatSheetRow[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/** Cross-game rollup. `season === null` parks the hook without a request. */
export function useSeasonRollup(
  apiClient: ApiClient,
  season: number | null,
  unit: StatUnit
): SeasonRollupState {
  const [feed, setFeed] = useState<SeasonRollupFeed | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (season === null) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getSeasonRollup({ season, unit }, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setError(null);
        setFeed(next);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(readableError(cause, 'season rollup'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, season, unit, refreshToken]);

  const rows = useMemo(() => toSeasonRows(feed, unit), [feed, unit]);
  return {
    feed,
    rows,
    isLoading,
    error,
    reload: useCallback(() => setRefreshToken((token) => token + 1), []),
  };
}

export interface SeasonOptionsState {
  options: number[];
  defaultSeason: number;
}

/**
 * Seasons the selector offers. Derived from the tenant's own event list rather
 * than a hardcoded range, with the active event's season and the current
 * calendar year folded in so the list is never empty.
 */
export function useSeasonOptions(
  apiClient: ApiClient,
  activeSeason: number | null,
  isSkipped: boolean
): SeasonOptionsState {
  const [fromEvents, setFromEvents] = useState<number[]>([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (isSkipped) return undefined;
    const controller = new AbortController();
    apiClient
      .listEvents({ limit: 100 }, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setFromEvents(
          page.items
            .map((item) => item.season_year)
            .filter((year): year is number => Number.isInteger(year))
        );
      })
      .catch(() => {
        // A failed season list is not worth an error banner: the fallback range
        // below still lets a coach pick a season.
      });
    return () => controller.abort();
  }, [apiClient, isSkipped]);

  return useMemo(() => {
    const fallback = Array.from({ length: SEASON_LOOKBACK }, (_unused, i) => currentYear - i);
    const all = new Set<number>([
      ...fromEvents,
      ...(activeSeason === null ? [] : [activeSeason]),
      ...fallback,
    ]);
    const options = [...all].sort((left, right) => right - left);
    return { options, defaultSeason: activeSeason ?? options[0] ?? currentYear };
  }, [activeSeason, currentYear, fromEvents]);
}
